import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CircleCheckBig } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PriorityList } from '@/components/kanban/priority-list'
import { IssueTable, TableSkeleton } from '@/components/tables/issue-table'
import { IssueDataTable } from '@/components/tables/data-table'
import { ISSUE_COLUMNS } from '@/components/tables/issue-columns'
import { ViewModeToggle } from '@/components/common/view-mode-toggle'
import { EmptyState } from '@/components/common/empty-state'
import { useViewMode } from '@/hooks/use-view-mode'
import { ViewHeader } from '@/components/shell/view-header'
import { useOpenIssueSheet } from '@/hooks/use-ui-state'
import { useIssues, useRepoMeta } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { useMe } from '@/hooks/use-me'
import { useContextOrder, useReorderContext } from '@/hooks/use-context-order'
import { EMPTY_ORDER } from '@/lib/context-order'
import { ME_VIEW } from '@/lib/views'

// Two lenses on a person's work: Assigned (their re-sortable priority list) and
// Reported (a read-only table of what they authored). Defaults to the signed-in
// user; a picker switches to anyone else's. You can reorder your own list; only
// the reorder team may reorder someone else's (enforced server-side).
export default function Me() {
  const me = useMe()!
  const { data: issues, isPending, isError, error, refetch } = useIssues()
  const people = useRepoMeta().data?.assignableUsers ?? []
  const openIssue = useOpenIssueSheet()
  const [searchParams, setSearchParams] = useSearchParams()

  const login = searchParams.get('user') ?? me.login
  const isSelf = login === me.login
  const canReorder = isSelf || me.canReorderOthers
  const person = people.find((p) => p.login === login)
  const name = person?.name ?? login

  const context = `mine/${login}`
  const order = useContextOrder(context).data ?? EMPTY_ORDER
  const reorder = useReorderContext(context)
  const [view, setView] = useViewMode()

  const mode = searchParams.get('mode') === 'reported' ? 'reported' : 'assigned'

  const assigned = useMemo(
    () => ME_VIEW.pool(issues ?? [], { viewerLogin: login }),
    [issues, login],
  )

  const reported = useMemo(
    () =>
      ME_VIEW.pool(issues ?? [], { viewerLogin: login, meMode: 'reported' })
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    [issues, login],
  )

  const setMode = (m: 'assigned' | 'reported') =>
    setSearchParams((p) => {
      const next = new URLSearchParams(p)
      if (m === 'assigned') next.delete('mode')
      else next.set('mode', m)
      return next
    }, { replace: true })

  const setUser = (next: string) =>
    setSearchParams((p) => {
      const params = new URLSearchParams(p)
      if (next === me.login) params.delete('user')
      else params.set('user', next)
      return params
    }, { replace: true })

  const tabs = (
    <div className="border-border bg-surface ml-2 flex items-center gap-px rounded-lg border p-0.5">
      {(
        [
          ['assigned', `Assigned (${assigned.length})`],
          ['reported', `Reported (${reported.length})`],
        ] as const
      ).map(([m, label]) => (
        <Button
          key={m}
          size="sm"
          variant={mode === m ? 'secondary' : 'ghost'}
          className="h-6 px-2.5 text-xs"
          onClick={() => setMode(m)}
        >
          {label}
        </Button>
      ))}
    </div>
  )

  const picker = people.length > 0 && (
    <Select value={login} onValueChange={setUser}>
      <SelectTrigger size="sm" className="h-6 w-40 text-xs">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={me.login}>Me</SelectItem>
        {/* A ?user= login that isn't an assignable user (e.g. deactivated) still
            needs an item so the trigger isn't blank. */}
        {!isSelf && !person && <SelectItem value={login}>{name}</SelectItem>}
        {people
          .filter((p) => p.login !== me.login)
          .map((p) => (
            <SelectItem key={p.login} value={p.login}>
              {p.name ?? p.login}
            </SelectItem>
          ))}
      </SelectContent>
    </Select>
  )

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title={isSelf ? 'Me' : `${name}’s issues`}
        meta={
          mode === 'assigned'
            ? `${assigned.length} assigned${
                view === 'table'
                  ? ''
                  : canReorder
                    ? ' · drag to re-rank, drag across bands to re-prioritise'
                    : ' · read-only (you can’t reorder others’ lists)'
              }`
            : `${reported.length} reported · most recently updated first`
        }
        chips={null}
        tabs={tabs}
        toolbar={
          <div className="flex items-center gap-1.5">
            {mode === 'assigned' && <ViewModeToggle mode={view} onChange={setView} />}
            {picker}
          </div>
        }
      />
      {isError ? (
        <ViewError what="these issues" error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        mode === 'assigned' && view === 'board' ? (
          <div className="mx-auto flex w-full max-w-2xl flex-col gap-2 p-4">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        ) : (
          <TableSkeleton />
        )
      ) : mode === 'assigned' ? (
        view === 'table' ? (
          <IssueDataTable
            issues={assigned}
            columns={ISSUE_COLUMNS}
            sortable
            bulkKind="work"
            emptyTitle={isSelf ? 'Nothing assigned to you' : `Nothing assigned to ${name}`}
          />
        ) : assigned.length === 0 ? (
          <EmptyState
            className="m-auto p-10"
            icon={<CircleCheckBig className="text-faint-foreground size-6" />}
            title={isSelf ? 'Nothing assigned to you' : `Nothing assigned to ${name}`}
          />
        ) : (
          <PriorityList
            issues={assigned}
            onOpen={openIssue}
            order={order}
            onReorder={reorder}
            readOnly={!canReorder}
            excludeContext={context}
          />
        )
      ) : (
        <IssueTable issues={reported} bulkKind="work" />
      )}
    </div>
  )
}
